import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Text,
  Table,
  Button,
  Card,
  SimpleGrid,
  Loader,
  Alert,
  Accordion,
  Badge,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconRefresh, IconAlertCircle, IconCoin, IconClock, IconUsers, IconFileDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { getPayrollPreview } from '../api/prediction-api';
import type { PayrollPreviewResponse } from '../types/prediction-types';

// Helper to calculate default pay period based on current date
function getDefaultPayPeriod() {
  const today = dayjs();
  const dayOfMonth = today.date();
  
  if (dayOfMonth > 15) {
    // After 15th → show 1st to 15th of current month
    return {
      start: today.startOf('month').toDate(),
      end: today.startOf('month').add(14, 'days').toDate(), // 15th
    };
  } else {
    // Between 1st-15th → show 16th to end of previous month
    const prevMonth = today.subtract(1, 'month');
    return {
      start: prevMonth.startOf('month').add(15, 'days').toDate(), // 16th
      end: prevMonth.endOf('month').toDate(),
    };
  }
}

export default function PayrollPreviewPage() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<PayrollPreviewResponse | null>(null);
  
  // Initialize with default pay period
  const defaultPeriod = getDefaultPayPeriod();
  const [startDate, setStartDate] = useState<Date | null>(defaultPeriod.start);
  const [endDate, setEndDate] = useState<Date | null>(defaultPeriod.end);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (startDate) params.start_date = dayjs(startDate).format('YYYY-MM-DD');
      if (endDate) params.end_date = dayjs(endDate).format('YYYY-MM-DD');

      const response = await getPayrollPreview(params);
      
      // SAFETY: Validate response structure
      if (!response || !response.success) {
        throw new Error('Invalid response from server');
      }
      
      setData(response);
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to load payroll data',
        color: 'red',
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExport = async () => {
    if (!data) {
      notifications.show({
        title: 'No Data',
        message: 'Please load payroll data first',
        color: 'orange',
      });
      return;
    }

    try {
      setExporting(true);
      
      // Call the backend export endpoint
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', dayjs(startDate).format('YYYY-MM-DD'));
      if (endDate) params.set('end_date', dayjs(endDate).format('YYYY-MM-DD'));
      
      const response = await fetch(`/export-payroll?${params}`);
      const result = await response.json();
      
      if (result.success && result.sheet_url) {
        notifications.show({
          title: 'Success!',
          message: 'Payroll exported to Google Sheets',
          color: 'green',
        });
        
        // Open the spreadsheet in a new tab
        window.open(result.sheet_url, '_blank');
      } else {
        throw new Error(result.errors?.join(', ') || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting payroll:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to export payroll',
        color: 'red',
      });
    } finally {
      setExporting(false);
    }
  };

  // SAFETY: Helper to get hours for employee on specific date
  const getHoursForDate = (employeeId: number, date: string): number => {
    if (!data || !Array.isArray(data.daily_hours)) return 0;
    
    const found = data.daily_hours.find(
      dh => dh.employee_id === employeeId && dh.date === date
    );
    return found?.hours || 0;
  };

  // SAFETY: Helper to chunk dates into 7-day periods
  const chunkDatesIntoWeeks = (dates: string[]): string[][] => {
    if (!Array.isArray(dates)) return [];
    
    const chunks: string[][] = [];
    for (let i = 0; i < dates.length; i += 7) {
      chunks.push(dates.slice(i, i + 7));
    }
    return chunks;
  };

  // SAFETY: Helper to get commission details for employee
  const getCommissionDetails = (employeeId: number) => {
    if (!data || !Array.isArray(data.commission_details)) return [];
    
    return data.commission_details.filter(cd => cd.employee_id === employeeId);
  };

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Group gap="xs">
          <Loader size="sm" />
          <Text>Loading payroll data...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between">
          <Title order={1}>Payroll Preview</Title>
        </Group>

        {/* Date Range Filter */}
        <Paper shadow="sm" p="lg" withBorder>
          <Group justify="space-between" wrap="wrap">
            <Group gap="md">
              <DatePickerInput
                label="Start Date"
                placeholder="Pay period start"
                value={startDate}
                onChange={setStartDate}
                clearable
              />
              <DatePickerInput
                label="End Date"
                placeholder="Pay period end"
                value={endDate}
                onChange={setEndDate}
                clearable
                minDate={startDate || undefined}
              />
            </Group>
            <Group gap="sm">
              <Button
                leftSection={<IconRefresh size={16} />}
                onClick={fetchData}
                loading={loading}
                variant="default"
              >
                Refresh
              </Button>
              <Button
                leftSection={<IconFileDownload size={16} />}
                onClick={handleExport}
                loading={exporting}
                disabled={!data}
              >
                Export
              </Button>
            </Group>
          </Group>
          {data?.period && (
            <Text size="sm" c="dimmed" mt="sm">
              Showing: {dayjs(data.period.start).format('MMM D, YYYY')} - {dayjs(data.period.end).format('MMM D, YYYY')}
            </Text>
          )}
        </Paper>

        {!data ? (
          <Alert icon={<IconAlertCircle size={16} />} title="No Data" color="blue">
            No payroll data available. Click Refresh to load the most recent pay period.
          </Alert>
        ) : (
          <Stack gap="lg">
            {/* Summary Cards */}
            {data.summary && (
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <Card shadow="sm" padding="lg" withBorder>
                  <Group gap="sm">
                    <IconUsers size={24} color="#228be6" />
                    <div>
                      <Text size="xs" c="dimmed">Total Employees</Text>
                      <Text size="xl" fw={700}>{data.summary.total_employees}</Text>
                    </div>
                  </Group>
                </Card>
                <Card shadow="sm" padding="lg" withBorder>
                  <Group gap="sm">
                    <IconClock size={24} color="#40c057" />
                    <div>
                      <Text size="xs" c="dimmed">Total Hours</Text>
                      <Text size="xl" fw={700}>{data.summary.total_hours.toFixed(2)}h</Text>
                    </div>
                  </Group>
                </Card>
                <Card shadow="sm" padding="lg" withBorder>
                  <Group gap="sm">
                    <IconCoin size={24} color="#fd7e14" />
                    <div>
                      <Text size="xs" c="dimmed">Total Commission</Text>
                      <Text size="xl" fw={700}>${data.summary.total_commission.toFixed(2)}</Text>
                    </div>
                  </Group>
                </Card>
              </SimpleGrid>
            )}

            {/* Hours Table (Pivot by Date) */}
            <Paper shadow="sm" p="lg" withBorder>
              <Title order={3} mb="md">Hours by Employee & Date</Title>
              
              {Array.isArray(data.employees) && data.employees.length > 0 ? (
                <Stack gap="xl">
                  {chunkDatesIntoWeeks(data.dates || []).map((weekDates, weekIndex) => (
                    <div key={weekIndex}>
                      {/* Week Header */}
                      <Group gap="xs" mb="sm">
                        <Badge size="lg" variant="filled">Week {weekIndex + 1}</Badge>
                        <Text size="sm" c="dimmed">
                          {dayjs(weekDates[0]).format('MMM D')} - {dayjs(weekDates[weekDates.length - 1]).format('MMM D, YYYY')}
                        </Text>
                      </Group>
                      
                      {/* Week Table */}
                      <div style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Employee</Table.Th>
                              {/* Date columns for this week */}
                              {weekDates.map((date) => (
                                <Table.Th key={date} style={{ textAlign: 'center', minWidth: 80 }}>
                                  {dayjs(date).format('MMM D')}
                                  <br />
                                  <Text size="xs" c="dimmed">{dayjs(date).format('ddd')}</Text>
                                </Table.Th>
                              ))}
                              <Table.Th style={{ textAlign: 'right' }}>Week Total</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {data.employees.map((emp) => {
                              // Calculate week total for this employee
                              const weekTotal = weekDates.reduce((sum, date) => {
                                return sum + getHoursForDate(emp.employee_id, date);
                              }, 0);
                              
                              return (
                                <Table.Tr key={emp.employee_id}>
                                  <Table.Td>
                                    <Text fw={500}>{emp.employee_name}</Text>
                                  </Table.Td>
                                  {/* Hours for each date in this week */}
                                  {weekDates.map((date) => {
                                    const hours = getHoursForDate(emp.employee_id, date);
                                    return (
                                      <Table.Td key={`${emp.employee_id}-${date}`} style={{ textAlign: 'center' }}>
                                        {hours > 0 ? (
                                          <Badge size="sm" variant="light">
                                            {hours.toFixed(2)}
                                          </Badge>
                                        ) : (
                                          <Text c="dimmed">—</Text>
                                        )}
                                      </Table.Td>
                                    );
                                  })}
                                  <Table.Td style={{ textAlign: 'right' }}>
                                    <Text fw={600}>{weekTotal.toFixed(2)}h</Text>
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </div>
                    </div>
                  ))}
                  
                  {/* Grand Total Table */}
                  {data.dates && data.dates.length > 7 && (
                    <div>
                      <Group gap="xs" mb="sm">
                        <Badge size="lg" variant="filled" color="green">Period Total</Badge>
                        <Text size="sm" c="dimmed">
                          {dayjs(data.period.start).format('MMM D')} - {dayjs(data.period.end).format('MMM D, YYYY')}
                        </Text>
                      </Group>
                      
                      <div style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Employee</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>Total Hours</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {data.employees.map((emp) => (
                              <Table.Tr key={emp.employee_id}>
                                <Table.Td>
                                  <Text fw={600}>{emp.employee_name}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Text fw={700} size="lg">{emp.total_hours.toFixed(2)}h</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </Stack>
              ) : (
                <Text c="dimmed">No employee hours data available</Text>
              )}
            </Paper>

            {/* Commission Table with Expandable Details */}
            <Paper shadow="sm" p="lg" withBorder>
              <Title order={3} mb="md">Commission Details</Title>
              
              {Array.isArray(data.employees) && data.employees.length > 0 ? (
                <Accordion variant="separated">
                  {data.employees.map((emp) => {
                    const commissionDetails = getCommissionDetails(emp.employee_id);
                    
                    return (
                      <Accordion.Item key={emp.employee_id} value={String(emp.employee_id)}>
                        <Accordion.Control>
                          <Group justify="space-between" pr="md">
                            <Text fw={500}>{emp.employee_name}</Text>
                            <Group gap="md">
                              <Badge size="lg" color="orange" variant="light">
                                ${emp.total_commission.toFixed(2)}
                              </Badge>
                              {commissionDetails.length > 0 && (
                                <Badge size="sm" variant="outline">
                                  {commissionDetails.length} intervals
                                </Badge>
                              )}
                            </Group>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          {commissionDetails.length > 0 ? (
                            <Table striped>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Date & Time</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Orders</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Net Sales</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Total Comm.</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Shared With</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Your Share</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {commissionDetails.map((detail, idx) => (
                                  <Table.Tr key={idx}>
                                    <Table.Td>
                                      <Text size="sm" fw={500}>
                                        {dayjs(detail.interval_date).format('MMM D')}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {detail.interval_start} - {detail.interval_end}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                      {detail.total_orders}
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                      ${detail.net_sales.toFixed(2)}
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                      ${detail.total_commission.toFixed(2)}
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                      <Badge size="sm" variant="light">
                                        {detail.employee_count} emp{detail.employee_count !== 1 ? 's' : ''}
                                      </Badge>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                      <Text fw={600} c="orange">
                                        ${detail.commission_share.toFixed(2)}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          ) : (
                            <Text c="dimmed" size="sm">No commission earned this period</Text>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              ) : (
                <Text c="dimmed">No commission data available</Text>
              )}
            </Paper>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
